/*
 * zodiac-preview.js — shared live-preview engine for the Starmap / Zodiac charm.
 *
 * Renders the personalized zodiac artwork entirely in-browser by mutating an
 * inline <svg> clone of the per-sign template, mirroring the factory pipeline
 * (automations/zodiac_svg_personalize.py):
 *   - radial gradient stops -> the 2 user colors at offsets 0 / .45 / .75 / 1
 *     with the pattern [color1, color2, color1, #e7e7e7]
 *   - the bottom <text> element -> Name (y=0) + Date (y=111.54), centered.
 *
 * No server, no canvas. Used by both the PDP builder and the bundle modal.
 */
(function () {
  'use strict';

  if (window.ZodiacPreview) return;

  var SVGNS = 'http://www.w3.org/2000/svg';
  // Calendar order (Jan -> Dec) — drives the sign-picker tile order.
  var SIGNS = [
    'capricorn', 'aquarius', 'pisces', 'aries', 'taurus', 'gemini',
    'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius'
  ];

  // Geometry constants — kept in sync with zodiac_svg_personalize.py
  var BG_COLOR = '#e7e7e7';
  var VIEWBOX_CENTER_X = 378.5;
  var NAME_BASELINE_Y = 844.19;
  var DATE_OFFSET_Y = 111.54;
  var GRADIENT_STOPS = [0, 0.45, 0.75, 1]; // offsets; colors = [c1, c2, c1, bg]

  var templateCache = {}; // sign -> Promise<SVGElement> (parsed, foreign-doc)
  var urls = {};          // sign -> asset url
  var uidCounter = 0;

  function configure(signSvgUrls) {
    if (!signSvgUrls) return;
    for (var k in signSvgUrls) {
      if (Object.prototype.hasOwnProperty.call(signSvgUrls, k)) urls[k] = signSvgUrls[k];
    }
  }

  function fetchTemplate(sign) {
    if (templateCache[sign]) return templateCache[sign];
    var url = urls[sign];
    if (!url) return Promise.reject(new Error('ZodiacPreview: no URL configured for "' + sign + '"'));
    var p = fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('ZodiacPreview: fetch failed for ' + sign);
        return r.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
        var svg = doc.querySelector('svg');
        if (!svg) throw new Error('ZodiacPreview: no <svg> in ' + sign);
        return svg;
      });
    templateCache[sign] = p;
    return p;
  }

  function preload(sign) {
    return fetchTemplate(sign).catch(function () { return null; });
  }

  function ensureFont() {
    if (document.fonts && document.fonts.load) {
      try { return document.fonts.load("132px 'Alga Medium'").catch(function () {}); } catch (e) {}
    }
    return Promise.resolve();
  }

  function applyColors(svg, color1, color2) {
    var grad = svg.querySelector('radialGradient');
    if (!grad) return;
    // Uniquify the gradient id so multiple previews never collide on #radial-gradient,
    // and point the circle at it via an inline fill (overrides the template's CSS class).
    var uid = 'zp-grad-' + (uidCounter++);
    grad.setAttribute('id', uid);
    var circle = svg.querySelector('circle');
    if (circle) circle.style.fill = 'url(#' + uid + ')';

    var colors = [color1, color2, color1, BG_COLOR];
    var stops = grad.querySelectorAll('stop');
    // Normalize to exactly 4 stops.
    while (stops.length > 4) { grad.removeChild(stops[stops.length - 1]); stops = grad.querySelectorAll('stop'); }
    while (stops.length < 4) {
      grad.appendChild(document.createElementNS(SVGNS, 'stop'));
      stops = grad.querySelectorAll('stop');
    }
    for (var i = 0; i < 4; i++) {
      var s = stops[i];
      s.setAttribute('offset', String(GRADIENT_STOPS[i]));
      s.setAttribute('stop-color', colors[i]);
      s.removeAttribute('stop-opacity');
      s.style.stopColor = colors[i];
      s.style.stopOpacity = '1';
    }
  }

  // Collapse the baked, per-letter-positioned title into a single centered tspan,
  // matching the factory (Pillow draws the title as one centered string). This avoids
  // the letter-spacing gaps that the absolute tspan x-positions produce in-browser.
  var TITLE_BASELINE_Y = 168.64;
  function applyTitle(svg) {
    var texts = svg.querySelectorAll('text');
    if (!texts.length) return;
    var title = texts[0]; // document order: [0]=sign title
    var full = title.textContent || '';
    while (title.firstChild) title.removeChild(title.firstChild);
    title.setAttribute('text-anchor', 'middle');
    title.setAttribute('transform', 'translate(' + VIEWBOX_CENTER_X + ' ' + TITLE_BASELINE_Y + ')');
    var ts = document.createElementNS(SVGNS, 'tspan');
    ts.setAttribute('x', '0');
    ts.setAttribute('y', '0');
    ts.textContent = full;
    title.appendChild(ts);
  }

  function applyText(svg, name, date) {
    var texts = svg.querySelectorAll('text');
    if (texts.length < 2) return;
    var bottom = texts[1]; // document order: [0]=sign title, [1]=name+date
    while (bottom.firstChild) bottom.removeChild(bottom.firstChild);
    bottom.setAttribute('text-anchor', 'middle');
    bottom.setAttribute('transform', 'translate(' + VIEWBOX_CENTER_X + ' ' + NAME_BASELINE_Y + ')');

    var nameT = document.createElementNS(SVGNS, 'tspan');
    nameT.setAttribute('x', '0');
    nameT.setAttribute('y', '0');
    nameT.textContent = name || '';
    bottom.appendChild(nameT);

    if (date) {
      var dateT = document.createElementNS(SVGNS, 'tspan');
      dateT.setAttribute('x', '0');
      dateT.setAttribute('y', String(DATE_OFFSET_Y));
      dateT.textContent = date;
      bottom.appendChild(dateT);
    }
  }

  function buildAria(state) {
    var label = 'Starmap charm preview';
    if (state.sign) label += ' for ' + state.sign.charAt(0).toUpperCase() + state.sign.slice(1);
    if (state.name) label += ', name ' + state.name;
    if (state.date) label += ', date ' + state.date;
    return label;
  }

  function mount(container, node) {
    if (container.replaceChildren) {
      container.replaceChildren(node);
    } else {
      container.innerHTML = '';
      container.appendChild(node);
    }
  }

  /**
   * render(container, { sign, name, date, color1, color2 })
   * Returns a Promise resolving to the mounted <svg> (or null on failure).
   */
  function render(container, state) {
    if (!container || !state || !state.sign) return Promise.resolve(null);
    return fetchTemplate(state.sign).then(function (template) {
      // importNode adopts the foreign-document node into the current document.
      var svg = document.importNode(template, true);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.style.width = '100%';
      svg.style.height = 'auto';
      svg.style.display = 'block';
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', buildAria(state));

      applyColors(svg, state.color1, state.color2);
      applyTitle(svg);
      applyText(svg, state.name, state.date);

      mount(container, svg);
      return svg;
    }).catch(function (err) {
      if (window.console && console.warn) console.warn(err);
      if (container && !container.querySelector('svg')) {
        container.innerHTML = '<div class="zodiac-preview__fallback">Preview unavailable</div>';
      }
      return null;
    });
  }

  window.ZodiacPreview = {
    SIGNS: SIGNS,
    configure: configure,
    preload: preload,
    ensureFont: ensureFont,
    render: render
  };
})();
